import React, { useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Keyboard,
  ScrollView,
  useWindowDimensions,
  Dimensions,
} from "react-native";
import { db } from "../firebase"; // Firebase config
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import DropDownPicker from "react-native-dropdown-picker";
import { AuthContext } from "../Context/AuthContext";
import sendEmployeeEmail from "../components/EmpMail"; // Import the email function
import { Entypo, Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import GlobalStyles from "../components/StylenColor";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { NetworkContext } from "../Context/NetworkContext";
import { useDesktopDetect, DesktopTheme } from "../components/web";

// Desktop Registration View Component
const DesktopRegistrationView = ({
  companyCode,
  setCompanyCode,
  companyName,
  companyError,
  fetchCompanyDetails,
  employeeId,
  setEmployeeId,
  name,
  setName,
  phone,
  setPhone,
  phoneError,
  checkPhoneExists,
  email,
  setEmail,
  emailError,
  checkEmailExists,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  department,
  setDepartment,
  openDepartment,
  setOpenDepartment,
  departmentOptions,
  topic,
  setTopic,
  openTopic,
  setOpenTopic,
  topicOptions,
  isLoading,
  handleRegister,
  navigation,
}) => {
  const [hoveredField, setHoveredField] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);

  return (
    <View style={desktopStyles.page}>
      <ScrollView style={desktopStyles.container} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={desktopStyles.header}>
          <View style={desktopStyles.headerLeft}>
            <Text style={desktopStyles.pageTitle}>Employee Registration</Text>
            <Text style={desktopStyles.pageSubtitle}>
              Register a new employee to the ESG platform
            </Text>
          </View>
        </View>

        {/* Registration Form Card */}
        <View style={desktopStyles.formCard}>
          <View style={desktopStyles.formHeader}>
            <Ionicons name="person-add-outline" size={24} color={DesktopTheme.colors.primary} />
            <Text style={desktopStyles.formTitle}>New Employee Details</Text>
          </View>

          <View style={desktopStyles.formContent}>
            {/* Two Column Layout */}
            <View style={desktopStyles.formRow}>
              {/* Left Column */}
              <View style={desktopStyles.formColumn}>
                {/* Company Code */}
                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Company Code *</Text>
                  {companyError ? (
                    <Text style={desktopStyles.fieldError}>{companyError}</Text>
                  ) : companyName ? (
                    <Text style={desktopStyles.fieldSuccess}>Company: {companyName}</Text>
                  ) : null}
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'companyCode' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="business-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={companyCode}
                      onChangeText={(text) => {
                        setCompanyCode(text);
                        if (text.trim() !== "") fetchCompanyDetails(text);
                      }}
                      style={desktopStyles.input}
                      placeholder="Enter company code"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      autoCapitalize="none"
                      onFocus={() => setHoveredField('companyCode')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>

                {/* Employee ID */}
                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Employee ID *</Text>
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'employeeId' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="id-card-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={employeeId}
                      onChangeText={setEmployeeId}
                      style={desktopStyles.input}
                      placeholder="Enter employee ID"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      onFocus={() => setHoveredField('employeeId')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>

                {/* Name */}
                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Full Name *</Text>
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'name' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="person-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={name}
                      onChangeText={setName}
                      style={desktopStyles.input}
                      placeholder="Enter full name"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      onFocus={() => setHoveredField('name')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>

                {/* Phone */}
                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Phone Number *</Text>
                  {phoneError ? (
                    <Text style={desktopStyles.fieldError}>{phoneError}</Text>
                  ) : null}
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'phone' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="call-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={phone}
                      onChangeText={(text) => {
                        setPhone(text);
                        checkPhoneExists(text);
                      }}
                      style={desktopStyles.input}
                      placeholder="Enter 10-digit phone number"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      keyboardType="numeric"
                      onFocus={() => setHoveredField('phone')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>
              </View>

              {/* Right Column */}
              <View style={desktopStyles.formColumn}>
                {/* Email */}
                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Email Address *</Text>
                  {emailError ? (
                    <Text style={desktopStyles.fieldError}>{emailError}</Text>
                  ) : null}
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'email' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="mail-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={email}
                      onChangeText={(text) => {
                        setEmail(text);
                        checkEmailExists(text);
                      }}
                      style={desktopStyles.input}
                      placeholder="Enter email address"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      keyboardType="email-address"
                      onFocus={() => setHoveredField('email')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>

                {/* Password */}
                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Password *</Text>
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'password' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="lock-closed-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={password}
                      onChangeText={setPassword}
                      style={[desktopStyles.input, { flex: 1 }]}
                      placeholder="Enter password"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      secureTextEntry={!showPassword}
                      onFocus={() => setHoveredField('password')}
                      onBlur={() => setHoveredField(null)}
                    />
                    <TouchableOpacity
                      style={desktopStyles.eyeBtn}
                      onPress={() => setShowPassword(!showPassword)}
                    >
                      <Ionicons
                        name={showPassword ? "eye-outline" : "eye-off-outline"}
                        size={20}
                        color={DesktopTheme.colors.textMuted}
                      />
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Department Dropdown */}
                <View style={[desktopStyles.inputGroup, { zIndex: openDepartment ? 2000 : 1000 }]}>
                  <Text style={desktopStyles.label}>Department *</Text>
                  <DropDownPicker
                    open={openDepartment}
                    value={department}
                    items={departmentOptions}
                    setOpen={(isOpen) => {
                      setOpenDepartment(isOpen);
                      if (isOpen) {
                        setOpenTopic(false);
                        Keyboard.dismiss();
                      }
                    }}
                    setValue={(val) => {
                      setDepartment(val);
                      setTopic(null);
                    }}
                    placeholder="Select a Department"
                    style={desktopStyles.dropdown}
                    dropDownContainerStyle={desktopStyles.dropdownContainer}
                    listMode="SCROLLVIEW"
                    textStyle={desktopStyles.dropdownText}
                    placeholderStyle={desktopStyles.dropdownPlaceholder}
                  />
                </View>

                {/* Topic Dropdown */}
                <View style={[desktopStyles.inputGroup, { zIndex: openTopic ? 2000 : 1000 }]}>
                  <Text style={desktopStyles.label}>Topics *</Text>
                  <DropDownPicker
                    open={openTopic}
                    value={topic}
                    items={topicOptions}
                    setOpen={setOpenTopic}
                    setValue={setTopic}
                    placeholder="Select Topics"
                    style={desktopStyles.dropdown}
                    dropDownContainerStyle={desktopStyles.dropdownContainer}
                    disabled={!department}
                    multiple
                    mode="BADGE"
                    listMode="SCROLLVIEW"
                    textStyle={desktopStyles.dropdownText}
                    placeholderStyle={desktopStyles.dropdownPlaceholder}
                    badgeStyle={desktopStyles.badge}
                    badgeTextStyle={desktopStyles.badgeText}
                  />
                </View>
              </View>
            </View>

            {/* Actions */}
            <View style={desktopStyles.actionsRow}>
              <TouchableOpacity
                style={[
                  desktopStyles.cancelBtn,
                  hoveredBtn === 'cancel' && desktopStyles.cancelBtnHover,
                ]}
                onPress={() => navigation.goBack()}
                onMouseEnter={() => setHoveredBtn('cancel')}
                onMouseLeave={() => setHoveredBtn(null)}
              >
                <Text style={desktopStyles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  desktopStyles.submitBtn,
                  hoveredBtn === 'submit' && desktopStyles.submitBtnHover,
                  isLoading && desktopStyles.submitBtnDisabled,
                ]}
                onPress={handleRegister}
                disabled={isLoading}
                onMouseEnter={() => setHoveredBtn('submit')}
                onMouseLeave={() => setHoveredBtn(null)}
              >
                {isLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <Ionicons name="person-add-outline" size={20} color="#fff" />
                    <Text style={desktopStyles.submitBtnText}>Register Employee</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Info Card */}
        <View style={desktopStyles.infoCard}>
          <Ionicons name="information-circle-outline" size={24} color={DesktopTheme.colors.info} />
          <View style={desktopStyles.infoContent}>
            <Text style={desktopStyles.infoTitle}>Registration Note</Text>
            <Text style={desktopStyles.infoText}>
              A welcome email with login credentials will be sent to the employee's email address after successful registration.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  container: {
    flex: 1,
    padding: DesktopTheme.spacing.lg,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    fontSize: DesktopTheme.typography.fontSize.h2,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  formCard: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.xl,
    ...DesktopTheme.shadows.card,
    marginBottom: DesktopTheme.spacing.lg,
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    padding: DesktopTheme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: DesktopTheme.colors.border,
  },
  formTitle: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
  },
  formContent: {
    padding: DesktopTheme.spacing.lg,
  },
  formRow: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.xl,
  },
  formColumn: {
    flex: 1,
  },
  inputGroup: {
    marginBottom: DesktopTheme.spacing.md,
  },
  label: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
    fontWeight: '600',
    marginBottom: DesktopTheme.spacing.xs,
  },
  fieldError: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.error,
    marginBottom: DesktopTheme.spacing.xs,
  },
  fieldSuccess: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.success,
    marginBottom: DesktopTheme.spacing.xs,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DesktopTheme.colors.contentBg,
    borderRadius: DesktopTheme.borderRadius.md,
    borderWidth: 1,
    borderColor: DesktopTheme.colors.border,
    paddingHorizontal: DesktopTheme.spacing.md,
  },
  inputWrapperFocused: {
    borderColor: DesktopTheme.colors.primary,
    backgroundColor: '#ffffff',
  },
  inputIcon: {
    marginRight: DesktopTheme.spacing.sm,
  },
  input: {
    flex: 1,
    height: 48,
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
    outlineStyle: 'none',
  },
  eyeBtn: {
    padding: DesktopTheme.spacing.xs,
  },
  dropdown: {
    backgroundColor: DesktopTheme.colors.contentBg,
    borderColor: DesktopTheme.colors.border,
    borderRadius: DesktopTheme.borderRadius.md,
    minHeight: 48,
  },
  dropdownContainer: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderColor: DesktopTheme.colors.border,
    borderRadius: DesktopTheme.borderRadius.md,
    ...DesktopTheme.shadows.md,
  },
  dropdownText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
  },
  dropdownPlaceholder: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  badge: {
    backgroundColor: DesktopTheme.colors.primaryLight,
    borderRadius: DesktopTheme.borderRadius.sm,
  },
  badgeText: {
    color: DesktopTheme.colors.primary,
    fontSize: DesktopTheme.typography.fontSize.small,
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: DesktopTheme.spacing.md,
    marginTop: DesktopTheme.spacing.lg,
    paddingTop: DesktopTheme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: DesktopTheme.colors.border,
  },
  cancelBtn: {
    paddingVertical: DesktopTheme.spacing.md,
    paddingHorizontal: DesktopTheme.spacing.xl,
    borderRadius: DesktopTheme.borderRadius.md,
    backgroundColor: DesktopTheme.colors.border,
  },
  cancelBtnHover: {
    backgroundColor: DesktopTheme.colors.textMuted,
  },
  cancelBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    fontWeight: '600',
  },
  submitBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    paddingVertical: DesktopTheme.spacing.md,
    paddingHorizontal: DesktopTheme.spacing.xl,
    borderRadius: DesktopTheme.borderRadius.md,
    backgroundColor: DesktopTheme.colors.primary,
  },
  submitBtnHover: {
    backgroundColor: DesktopTheme.colors.primaryHover,
  },
  submitBtnDisabled: {
    backgroundColor: DesktopTheme.colors.textMuted,
  },
  submitBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: '#ffffff',
    fontWeight: '600',
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: DesktopTheme.spacing.md,
    backgroundColor: DesktopTheme.colors.info + '15',
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.md,
    borderWidth: 1,
    borderColor: DesktopTheme.colors.info + '30',
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.info,
    fontWeight: '600',
    marginBottom: 4,
  },
  infoText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    lineHeight: 22,
  },
});

const EmployeeRegistrationScreen = ({ navigation }) => {
  const [companyCode, setCompanyCode] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const { width } = useWindowDimensions();
  const isDesktop = Platform.OS === "web" && width >= 768;
  const isDesktopWeb = useDesktopDetect();
  const [employeeId, setEmployeeId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [department, setDepartment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [topic, setTopic] = useState(null);
  const [openDepartment, setOpenDepartment] = useState(false);
  const [openTopic, setOpenTopic] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [departmentData, setDepartmentData] = useState({});
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [companyError, setCompanyError] = useState("");
  const { isConnected } = useContext(NetworkContext);

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      showAlert(title, message);
    }
  };

  const fetchCompanyDetails = async (code) => {
    const q = query(
      collection(db, "companies"),
      where("companyCode", "==", code)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      setCompanyId(docSnap.id);
      setCompanyName(docSnap.data().name);
      setCompanyError("");
    } else {
      setCompanyId("");
      setCompanyName("");
      setCompanyError("Company not found for this code");
    }
  };

  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        const docRef = doc(db, "Dept&Topic", "departments");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setDepartmentData(data);
          const departments = Object.keys(data).map((dept) => ({
            label: dept,
            value: dept,
          }));
          setDepartmentOptions(departments);
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching department data:", error);
      }
    };
    fetchDepartmentData();
  }, []);

  useEffect(() => {
    if (department && departmentData[department]) {
      const topics = departmentData[department].map((topic) => ({
        label: topic,
        value: topic,
      }));
      setTopicOptions(topics);
    } else {
      setTopicOptions([]);
    }
  }, [department, departmentData]);

  const checkEmailExists = async (inputEmail) => {
    if (!inputEmail) return;
    const q = query(
      collection(db, `companies/${companyId}/employees`),
      where("email", "==", inputEmail)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      setEmailError("This email is already in use");
    } else {
      setEmailError("");
    }
  };

  const checkPhoneExists = async (phone) => {
    const q = query(
      collection(db, `companies/${companyId}/employees`),
      where("phone", "==", phone)
    );
    const querySnapshot = await getDocs(q);
    setPhoneError(
      querySnapshot.empty ? "" : "This phone number is already in use"
    );
  };

  const validateInputs = async () => {
    if (
      !companyCode ||
      !companyId ||
      !employeeId ||
      !name ||
      !phone ||
      !email ||
      !password ||
      !department ||
      !topic
    ) {
      showAlert("Error", "All fields are required!");
      return false;
    }
    if (companyError) {
      showAlert("Error", companyError);
      return false;
    }
    if (!/^\d{10}$/.test(phone)) {
      showAlert("Error", "Phone number must be 10 digits!");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      showAlert("Error", "Please enter a valid email address!");
      return false;
    }
    await checkEmailExists(email);
    await checkPhoneExists(phone);

    if (emailError || phoneError) {
      showAlert("Error", emailError || phoneError);
      return false;
    }

    const q = query(
      collection(db, `companies/${companyId}/employees`),
      where("employeeId", "==", employeeId)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      showAlert(
        "Error",
        "Employee ID already exists. Choose a different ID."
      );
      return false;
    }
    return true;
  };

  const handleRegister = async () => {
    if (isLoading) return;
    setIsLoading(true);
    const isValid = await validateInputs();
    if (!isValid) {
      setIsLoading(false);
      return;
    }
    if (!isConnected) {
      showAlert("No Internet", "You're currently offline.");
      return;
    }
    try {
      await addDoc(collection(db, `companies/${companyId}/employees`), {
        companyId,
        companyName,
        employeeId,
        name,
        phone,
        email,
        password,
        department,
        topic,
      });

      // Send Welcome Email
      await sendEmployeeEmail(
        email,
        name,
        employeeId,
        department,
        password,
        companyName,
        companyCode
      );

      showAlert("Success", "Employee registered successfully!");
      navigation.goBack();
    } catch (error) {
      console.error("Error registering employee:", error);
      showAlert("Error", "Failed to register employee. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Desktop Web View
  if (isDesktopWeb) {
    return (
      <DesktopRegistrationView
        companyCode={companyCode}
        setCompanyCode={setCompanyCode}
        companyName={companyName}
        companyError={companyError}
        fetchCompanyDetails={fetchCompanyDetails}
        employeeId={employeeId}
        setEmployeeId={setEmployeeId}
        name={name}
        setName={setName}
        phone={phone}
        setPhone={setPhone}
        phoneError={phoneError}
        checkPhoneExists={checkPhoneExists}
        email={email}
        setEmail={setEmail}
        emailError={emailError}
        checkEmailExists={checkEmailExists}
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        department={department}
        setDepartment={setDepartment}
        openDepartment={openDepartment}
        setOpenDepartment={setOpenDepartment}
        departmentOptions={departmentOptions}
        topic={topic}
        setTopic={setTopic}
        openTopic={openTopic}
        setOpenTopic={setOpenTopic}
        topicOptions={topicOptions}
        isLoading={isLoading}
        handleRegister={handleRegister}
        navigation={navigation}
      />
    );
  }

  // Mobile View
  return (
    <LinearGradient // Apply LinearGradient to the entire screen
      colors={GlobalStyles.GlobalBg.gradientColors}
      style={{ flex: 1 }}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <ScrollView
        style={{ height: "100%" }}
        contentContainerStyle={{
          padding: 16,
          paddingBottom: isDesktop ? 160 : 100, // more padding for desktop
          alignItems: "center",
        }}
      >
        <View style={[styles.container, isDesktop && styles.containerDesktop]}>
          <Text
            style={[
              styles.title,
              !isDesktop && { paddingTop: 30 },
              GlobalStyles.titleTxtOffset,
              {
                fontSize: isDesktop
                  ? GlobalStyles.fontSize.xxl
                  : GlobalStyles.fontSize.xxxl,
              },
            ]}
          >
            Employee Registration
          </Text>

          <View style={styles.formWrapper}>
            {companyError ? (
              <Text style={styles.errorText}>{companyError}</Text>
            ) : null}
            <TextInput
              style={[
                styles.input,
                {
                  fontSize: isDesktop
                    ? GlobalStyles.fontSize.xs
                    : GlobalStyles.fontSize.xss,
                  height: isDesktop ? 50 : 45,
                },
              ]}
              placeholder="Company Code"
              value={companyCode}
              onChangeText={(text) => {
                setCompanyCode(text);
                if (text.trim() !== "") fetchCompanyDetails(text); // fetch company by code now
              }}
              autoCapitalize="none"
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            <TextInput
              style={[
                styles.input,
                {
                  fontSize: isDesktop
                    ? GlobalStyles.fontSize.xs - 1
                    : GlobalStyles.fontSize.xss,
                  height: isDesktop ? 50 : 45,
                },
              ]}
              placeholder="Employee ID"
              value={employeeId}
              onChangeText={setEmployeeId}
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            <TextInput
              style={[
                styles.input,
                {
                  fontSize: isDesktop
                    ? GlobalStyles.fontSize.xs - 1
                    : GlobalStyles.fontSize.xss,
                  height: isDesktop ? 50 : 45,
                },
              ]}
              placeholder="Name"
              value={name}
              onChangeText={setName}
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            {phoneError ? (
              <Text style={styles.errorText}>{phoneError}</Text>
            ) : null}

            <TextInput
              style={[
                styles.input,
                {
                  fontSize: isDesktop
                    ? GlobalStyles.fontSize.xs - 1
                    : GlobalStyles.fontSize.xss,
                  height: isDesktop ? 50 : 45,
                },
              ]}
              placeholder="Phone Number"
              value={phone}
              onChangeText={(text) => {
                setPhone(text);
                checkPhoneExists(text);
              }}
              keyboardType="numeric"
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            {emailError ? (
              <Text style={styles.errorText}>{emailError}</Text>
            ) : null}

            <TextInput
              style={[
                styles.input,
                {
                  fontSize: isDesktop
                    ? GlobalStyles.fontSize.xs - 1
                    : GlobalStyles.fontSize.xss,
                  height: isDesktop ? 50 : 45,
                },
              ]}
              placeholder="Email"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                checkEmailExists(text);
              }}
              keyboardType="email-address"
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            {/* Password Field */}
            <View style={styles.passwordContainer}>
              <TextInput
                style={[
                  styles.passwordInput,
                  {
                    fontSize: isDesktop
                      ? GlobalStyles.fontSize.xs - 1
                      : GlobalStyles.fontSize.xss,
                    height: isDesktop ? 50 : 45,
                  },
                ]}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                selectionColor={GlobalStyles.inpColor.colors.selection}
              />
              <TouchableOpacity
                style={styles.eyeIcon}
                onPress={() => setShowPassword(!showPassword)}
              >
                <Entypo
                  name={showPassword ? "eye" : "eye-with-line"}
                  size={24}
                  color="black"
                />
              </TouchableOpacity>
            </View>

            {/* Department Dropdown */}
            <View
              style={[
                styles.dropdownContainer,
                { zIndex: openDepartment ? 2000 : 1000 },
              ]}
            >
              <DropDownPicker
                open={openDepartment}
                value={department}
                items={departmentOptions}
                setOpen={(isOpen) => {
                  setOpenDepartment(isOpen);
                  if (isOpen) {
                    setOpenTopic(false);
                    Keyboard.dismiss();
                  }
                }}
                setValue={(val) => {
                  setDepartment(val);
                  setTopic(null);
                }}
                placeholder="Select a Department"
                placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                containerStyle={styles.dropdown}
                listMode="SCROLLVIEW"
                modalContentContainerStyle={
                  GlobalStyles.dropdown.modalContentContainerStyle
                }
                textStyle={GlobalStyles.dropdown.textStyle}
                labelStyle={GlobalStyles.dropdown.labelStyle}
                placeholderStyle={GlobalStyles.dropdown.placeholderStyle}
              />
            </View>

            {/* Topic Dropdown */}
            <View
              style={[
                styles.dropdownContainer,
                { zIndex: openTopic ? 2000 : 1000 },
              ]}
            >
              <DropDownPicker
                open={openTopic}
                value={topic}
                items={topicOptions}
                setOpen={setOpenTopic}
                setValue={setTopic}
                placeholder="Select Topics"
                placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                containerStyle={styles.dropdown}
                disabled={!department}
                multiple
                mode="BADGE"
                listMode="SCROLLVIEW"
                modalContentContainerStyle={
                  GlobalStyles.dropdown.modalContentContainerStyle
                }
                textStyle={GlobalStyles.dropdown.textStyle}
                labelStyle={GlobalStyles.dropdown.labelStyle}
                placeholderStyle={GlobalStyles.dropdown.placeholderStyle}
              />

              {/* Register Button */}
              {isLoading ? (
                <ActivityIndicator size="large" color="#007BFF" />
              ) : (
                <TouchableOpacity
                  style={[styles.RegButton, GlobalStyles.btnBgShadow]}
                  onPress={handleRegister}
                >
                  <Text
                    style={[
                      styles.RegButtonText,
                      GlobalStyles.btntxtoffset,
                      {
                        fontSize: isDesktop
                          ? GlobalStyles.fontSize.sm
                          : GlobalStyles.fontSize.md,
                      },
                    ]}
                  >
                    Register
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
  },
  webScrollContainer: {
    flex: 1,
    height: "100%",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
  formWrapper: {
    width: "100%", // Ensures inputs take full width of the container
    alignItems: "center",
  },
  container: {
    width: "100%",
    maxWidth: 500, // Maximum width for the form container
    backgroundColor: "#bdd9c2", // Slightly transparent background for the form
    borderRadius: 12,
    padding: 25, // Increased padding
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    alignItems: "center", // Center content horizontally within the form box
  },
  title: {
    fontSize: GlobalStyles.fontSize.xxxl,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    color: GlobalStyles.btnColor.backgroundColor,
    marginBottom: 20,
    paddingVertical: 1,
  },
  input: {
    color: "#000",
    width: "100%",
    height: 45,
    height: hp("5.5%"),
    fontSize: GlobalStyles.fontSize.xss,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 16,
    paddingHorizontal: 12,
    backgroundColor: "#fff",
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  dropdownContainer: {
    width: "100%",
    marginBottom: 0,
    alignItems: "center",
  },
  dropdown: {
    width: "100%",
    marginBottom: 16,
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  passwordContainer: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  passwordInput: {
    flex: 1,
    height: 45,
    borderColor: "#ccc",
    height: hp("5.5%"),
    fontSize: GlobalStyles.fontSize.xss,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    backgroundColor: "#fff",
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  eyeIcon: {
    position: "absolute",
    right: 10,
    padding: 10,
  },
  RegButton: {
    width: "100%",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
    marginBottom: 16,
  },
  RegButtonText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.md,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    paddingVertical: 1,
  },
  errorText: {
    color: "red",
    fontSize: GlobalStyles.fontSize.xsss,
    paddingVertical: 1,
    alignSelf: "flex-start",
    marginBottom: 8,
    marginLeft: 10,
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
});

export default EmployeeRegistrationScreen;
